import { memo, useCallback, useState } from 'react';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { FooterMenu } from './FooterMenu';
import { CommentList } from './CommentList';
import { FileList } from './FileList/FileList';
import type { CurrentMenu } from './HeaderMenuFirst/HeaderMenuRight/HeaderMenuRight';
import { NotificationList } from '@user/notification';
import type { WorkspaceEntity } from '@workspace/domain/workspace';
import { HeaderMenuSecond } from './HeaderMenuSecond';
import { HeaderMenuFirst } from './HeaderMenuFirst';
import type { ViewModelPermissions } from '@view-model/application/context';
import { useUserType } from '@framework/auth/authStatus';

// NOTE: この型を変更した場合には、メモ化のために propsAreEqual関数も変更する必要あり
type Props = {
  setClientViewBox: (viewBox: Rect) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  workspace: WorkspaceEntity;
  viewModel: ViewModelEntity;
  viewModelPermissions: ViewModelPermissions;
  onGenerateLink(): void;
  onExportViewModelAsImage(isWhole: boolean): void;
};

const propsAreEqual = (prev: Props, next: Props) => {
  return (
    prev.setClientViewBox === next.setClientViewBox &&
    prev.onZoomIn === next.onZoomIn &&
    prev.onZoomOut === next.onZoomOut &&
    prev.onZoomReset === next.onZoomReset &&
    prev.workspace.isEqual(next.workspace) &&
    prev.viewModel === next.viewModel &&
    prev.viewModelPermissions === next.viewModelPermissions &&
    prev.onGenerateLink === next.onGenerateLink &&
    prev.onExportViewModelAsImage === next.onExportViewModelAsImage
  );
};

const FloatingUIContainerFn: React.FC<Props> = ({
  setClientViewBox,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  workspace,
  viewModel,
  viewModelPermissions,
  onGenerateLink,
  onExportViewModelAsImage,
}: Props) => {
  const [currentMenu, setCurrentMenu] = useState<CurrentMenu>('none');
  const onNotificationListClose = useCallback(() => setCurrentMenu('none'), []);
  const userType = useUserType();
  const canUploadFile = viewModelPermissions.isContentEditable && userType !== 'anonymous';

  return (
    <div className="pointer-events-none absolute left-0 top-0 flex size-full flex-col gap-4 py-4 pl-4 pr-8">
      {/* 上部のメニュー要素を配置する flex コンテナ。 */}
      <div className="flex flex-row justify-between gap-4">
        <HeaderMenuFirst
          currentMenu={currentMenu}
          setCurrentMenu={setCurrentMenu}
          workspace={workspace}
          viewModel={viewModel}
          viewModelPermissions={viewModelPermissions}
          setClientViewBox={setClientViewBox}
          onGenerateLink={onGenerateLink}
          onExportViewModelAsImage={onExportViewModelAsImage}
        />
      </div>

      <div className="flex flex-row justify-between gap-3">
        <HeaderMenuSecond workspace={workspace} viewModel={viewModel} showBannerAnnouncement={currentMenu === 'none'} />
      </div>

      {/* 左上のメニューが長い時にコメントリストの上部までメニューが伸ばせるように、上下でflexコンテナを分割している。 */}
      {currentMenu === 'files' && (
        <div className="mb-auto ml-auto flex justify-end overflow-y-hidden rounded-lg shadow-xl">
          <div className="max-h-full">
            <FileList
              groupId={workspace.groupId}
              viewModelId={viewModel.id}
              canUploadFile={canUploadFile}
              onClose={() => setCurrentMenu('none')}
            />
          </div>
        </div>
      )}

      {currentMenu === 'comments' && (
        <div className="mb-auto ml-auto flex justify-end overflow-y-hidden rounded-lg shadow-xl">
          <div className="max-h-full">
            <CommentList viewModelId={viewModel.id} onClose={() => setCurrentMenu('none')} />
          </div>
        </div>
      )}

      {currentMenu === 'notifications' && (
        <div className="mb-auto ml-auto flex justify-end overflow-y-hidden rounded-lg shadow-xl">
          <NotificationList onClose={onNotificationListClose} />
        </div>
      )}

      <div className="mt-auto flex justify-end">
        <FooterMenu onZoomIn={onZoomIn} onZoomOut={onZoomOut} onZoomReset={onZoomReset} />
      </div>
    </div>
  );
};

export const FloatingUIContainer = memo(FloatingUIContainerFn, propsAreEqual);
